import { virtualize } from "@lit-labs/virtualizer/virtualize.js";
import {
    Absence,
    AbsenceStatus,
    type BonusResult,
    type BonusType,
    type DailyResultsDTO,
    type DateRange,
    GetAbsencesParams,
    GetDailyResultsParams,
    GetRevenueEntriesParams,
    type Issue,
    IssueType,
    type RevenueEntry,
    RevenueType,
    TimeEntry,
    TimeEntryType,
    absenceTypes,
    applyPastDateFlip,
    assignEmployee,
    createAbsenceHoursGetter,
    dateAdd,
    formatDate,
    formatNumber,
    getDailyResults,
    getHolidayForDate,
    getNominalTime,
    getPayrollReport,
    getRange,
    getResultBreakDown,
    getSalaryOrDefault,
    getShiftIssues,
    getShiftsForIssue,
    getStatutoryBreak,
    isHalfDayEntry,
    moveTimesToDate,
    timeEntryTypeColor,
    toDateString,
    toDurationString,
    toTimeString,
} from "@pentacode/core";
import { type DateString, type Hours, add } from "@pentacode/openapi";
import "@pentacode/ui/components";
import { LitElement, css, html, nothing } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { cache } from "lit/directives/cache.js";
import { classMap } from "lit/directives/class-map.js";
import { live } from "lit/directives/live.js";
import { popover } from "../../../directives/popover";
import "../../../elements/absence-dialog";
import type { AbsenceDialog } from "../../../elements/absence-dialog";
import { alert, confirm } from "../../../elements/alert-dialog";
import "../../../elements/date-range-picker";
import type { DateRangePicker } from "../../../elements/date-range-picker";
import "../../../elements/employee-day";
import type { EmployeeDay } from "../../../elements/employee-day";
import "../../../elements/month-picker";
import "../../../elements/popover";
import "../../../elements/scroller";
import { app } from "../../../init";
import { setVirtualizedDragImage } from "../../../lib/drag-image";
import { DropGuard } from "../../../lib/drop-guard";
import { formatFraction } from "../../../lib/lit-dependent.util";
import { singleton } from "../../../lib/singleton";
import { debounce, isCursorInInput, isSafari } from "../../../lib/util";
import { Routing, routeProperty } from "../../../mixins/routing";
import { StateMixin } from "../../../mixins/state";
import { VirtualizerMixin } from "../../../mixins/virtualizer";
import { shared } from "../../../styles";
import { getRangesForLocalResultCalculation } from "../../../utils";
import "../../settings/components/bonus-type-item";
import { isCommitted, timeEntryDropBlockedReason } from "../committed-time-entries";
import { defaultBookingDate } from "../default-booking-date";
import "./components/hour-adjustment-dialog";
import type { HourAdjustmentDialog } from "./components/hour-adjustment-dialog";
import "./components/payroll-report";
import "./components/vacation-adjustment-dialog";
import type { VacationAdjustmentDialog } from "./components/vacation-adjustment-dialog";

interface Item {
    date: DateString;
    timeEntry?: TimeEntry;
    absence?: Absence;
}

interface DragData {
    entry?: Partial<TimeEntry>;
}

function sortTypeEntriesByTime(a: TimeEntry, b: TimeEntry) {
    // handle standard case first where days are different and apply linear sorting
    const dayA = toDateString(a.start);
    const dayB = toDateString(b.start);

    const defaultOrder = a.start.getTime() - b.start.getTime();

    if (dayA !== dayB) {
        return defaultOrder;
    }

    // handle absence cases
    const isAbsence = (type: TimeEntryType) => absenceTypes.some((absenceType) => absenceType === type);

    const isEntryBeforeNoon = (time: Date) => time.getHours() < 12;
    const isShiftBeforeNoon = (entry: TimeEntry) => isEntryBeforeNoon(entry.start) && !isAbsence(entry.type);
    // valid case would be when at least one absence and one shift before noon
    if ((isAbsence(a.type) || isAbsence(b.type)) && (isShiftBeforeNoon(a) || isShiftBeforeNoon(b))) {
        return isAbsence(b.type) ? -1 : 1;
    }

    // apply the default sorting as fallback
    return defaultOrder;
}

@customElement("ptc-employees-time-single")
export class EmployeesTimeSingle extends VirtualizerMixin(Routing(StateMixin(LitElement))) {
    routePattern = /^employees\/(?<id>\d+)\/time$/;

    get routeTitle() {
        return `Arbeitszeiten: ${this._employee && this._employee.name}`;
    }

    @routeProperty({ arg: "id", type: Number })
    employee: number;

    @routeProperty({ type: String, param: "timeEntry" })
    private _activeTimeEntryId?: string;

    protected defaultRange = () => getRange(new Date(), "month");
    protected rangeChanged = () => this.synchronize();

    @state()
    private _timeEntries: TimeEntry[] = [];

    @state()
    private _advances: RevenueEntry[] = [];

    @state()
    private _loading = false;

    @state()
    private _absences: Absence[] = [];

    @state()
    private _activeDate: DateString | null = null;

    @state()
    private _activeEntry: string | null = null;

    @state()
    private _activeField: string | null = null;

    @state()
    private _dragData: DragData | null = null;

    private _dropGuard = new DropGuard(this);

    @state()
    private _items: Item[] = [];

    @state()
    private _issues: Issue[] = [];

    @state()
    private _prevResults: DailyResultsDTO[] = [];

    @singleton("ptc-hour-adjustment-dialog")
    private _hourAdjustmentDialog: HourAdjustmentDialog;

    @singleton("ptc-vacation-adjustment-dialog")
    private _vacationAdjustmentDialog: VacationAdjustmentDialog;

    @singleton("ptc-absence-dialog")
    private _absenceDialog: AbsenceDialog;

    @query("ptc-employee-day")
    private _employeeDayForm: EmployeeDay;

    // The employee-day stats cache per employee/month/year, so invalidate them whenever the data
    // behind the balances changes. Debounced to let the background server sync settle first.
    private _loadStats = debounce(() => this._employeeDayForm?.loadStats(), 500);

    @query("ptc-date-range-picker")
    private _dateRangePicker: DateRangePicker;

    private _highlightedEntryId: string | null = null;

    private _highlightTimeEntry(timeEntryId: string) {
        const timeEntryRow = this.shadowRoot?.getElementById(`entry-${timeEntryId}`) as HTMLDivElement;

        if (timeEntryRow) {
            this._highlightedEntryId = timeEntryId;
            timeEntryRow.classList.add("highlight");
            timeEntryRow.scrollIntoView({ behavior: "smooth", block: "center" });
            timeEntryRow.click();

            setTimeout(() => {
                timeEntryRow.classList.remove("highlight");
            }, 2000);
        }
    }

    private get _employee() {
        return (this.employee && app.getEmployee(this.employee)) || null;
    }

    handleRoute() {
        // synchronize in the background
        void this.synchronize(true);
    }

    private _keydownHandler = (e: KeyboardEvent) => {
        if (
            !this.active ||
            e.ctrlKey ||
            e.metaKey ||
            (!this._employeeDayForm?.matches(":focus-within") && isCursorInInput())
        ) {
            return;
        }
        const direction = { w: "up", s: "down", a: "left", d: "right" }[e.key] as "up" | "down" | "left" | "right";
        if (direction === "left") {
            this._dateRangePicker.previousRange();
            this._setActive({ date: null });
        } else if (direction === "right") {
            this._dateRangePicker.nextRange();
            this._setActive({ date: null });
        } else if (direction === "down" || direction === "up") {
            e.preventDefault();
            const index =
                this._activeEntry && this._activeEntry !== "new"
                    ? this._items.findIndex((i) => i.timeEntry?.id === this._activeEntry)
                    : this._activeDate
                      ? this._items.findIndex((i) => i.date === this._activeDate)
                      : direction === "down"
                        ? -1
                        : this._items.length;
            const item =
                this._items[index + (direction === "down" ? 1 : -1)] ||
                (direction === "down" ? this._items[0] : this._items[this._items.length - 1]);
            this._setActive({ date: item?.date, entry: item?.timeEntry?.id || null });
        } else if (e.key === "Escape") {
            this._setActive({ date: null, entry: null }, true);
        } else if (e.shiftKey && e.key === "Backspace") {
            const activeEntry = this._timeEntries.find((e) => e.id === this._activeEntry);
            if (activeEntry) {
                void this._removeTimeEntry(activeEntry);
            }
        } else if (e.key === "n") {
            this._setActive({ entry: "new" });
        }
    };

    private _dragoverHandler = (e: DragEvent) => this._dragover(e);

    private _dropHandler = (e: DragEvent) => e.preventDefault();

    private _dragendHandler = (e: DragEvent) => this._dragend(e);

    connectedCallback() {
        super.connectedCallback();
        this.addEventListener("dragover", this._dragoverHandler);
        this.addEventListener("drop", this._dropHandler);
        this.addEventListener("dragend", this._dragendHandler);
        document.addEventListener("keydown", this._keydownHandler);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener("dragover", this._dragoverHandler);
        this.removeEventListener("drop", this._dropHandler);
        this.removeEventListener("dragend", this._dragendHandler);
        document.removeEventListener("keydown", this._keydownHandler);
    }

    async synchronize(showSpinner = false) {
        if (!this.dateRange || !this.employee) {
            return;
        }

        if (showSpinner) {
            // this._items = [];
            this._loading = true;
        }

        const { from, to } = this.dateRange;
        const ranges = getRangesForLocalResultCalculation(this.dateRange, app.company.settings.startPayrollPeriod);

        try {
            const [timeEntries, absences, advances, prevResults] = await Promise.all([
                app.getTimeEntries({
                    ...ranges.timeEntries,
                    employee: this.employee,
                    type: [
                        TimeEntryType.Work,
                        TimeEntryType.Vacation,
                        TimeEntryType.Sick,
                        TimeEntryType.HourAdjustment,
                        TimeEntryType.VacationAdjustment,
                        TimeEntryType.CompDay,
                        TimeEntryType.ChildSick,
                        TimeEntryType.SickInKUG,
                        TimeEntryType.Free,
                    ],
                }),
                app.api.getAbsences(new GetAbsencesParams({ from, to, employee: [this.employee] })),
                app.hasPermission("manage.employees.advances")
                    ? app.api.getRevenueEntries(
                          new GetRevenueEntriesParams({
                              from,
                              to,
                              employee: this.employee,
                              type: [RevenueType.PayAdvance],
                          })
                      )
                    : Promise.resolve([]),
                app.api.getDailyResults(
                    new GetDailyResultsParams({
                        ...ranges.prevDailyResults,
                        filters: [{ type: "employeeId", value: this.employee }],
                    })
                ),
            ]);

            this._timeEntries = timeEntries.sort(sortTypeEntriesByTime);
            this._absences = absences;
            this._advances = advances;
            this._prevResults = prevResults;
            this._refresh();
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        if (showSpinner) {
            this._loading = false;
        }
    }

    private _dropBlockedReason(e: DragEvent, date: DateString) {
        const entry = this._dragData?.entry;
        if (!entry) {
            return null;
        }
        const copies = !entry.id || e.altKey;
        return timeEntryDropBlockedReason(
            copies ? undefined : entry.date,
            date,
            app.company?.settings.commitTimeEntriesBefore
        );
    }

    private _dragenter(e: DragEvent, date: DateString) {
        if (this._dropGuard.check(e, this._dropBlockedReason(e, date))) {
            return;
        }
        e.preventDefault();
        (e.target as HTMLElement).classList.add("dragover");
    }

    private _dragoverDay(e: DragEvent, date: DateString) {
        if (!this._dropGuard.check(e, this._dropBlockedReason(e, date))) {
            this._dragover(e);
        }
    }

    private _dragover(e: DragEvent) {
        e.preventDefault();
        if (!isSafari) {
            e.dataTransfer!.dropEffect = !e.altKey ? "link" : "copy";
        }
    }

    private _dragleave(e: DragEvent) {
        (e.target as HTMLElement).classList.remove("dragover");
        this._dropGuard.leave(e);
    }

    private _dragend(e?: DragEvent) {
        this._dragData = null;
        this.classList.remove("dragging");
        for (const el of [this, ...this.renderRoot!.querySelectorAll(".dragging")] as HTMLElement[]) {
            el.classList.remove("dragging");
        }
        e && e.preventDefault();
    }

    private _dropIntoDay(e: DragEvent, date: DateString) {
        e.preventDefault();

        const data = this._dragData;

        if (!data || !this._employee) {
            return;
        }

        if (!data.entry) {
            return;
        }

        if (this._dropBlockedReason(e, date)) {
            return;
        }

        const { id, type = TimeEntryType.Work, position, breakPlanned } = data.entry;
        const { startPlanned, endPlanned, startFinal, endFinal } = moveTimesToDate(data.entry, date);

        if (!data.entry.id || e.altKey) {
            const times = applyPastDateFlip(date, {
                startPlanned,
                endPlanned,
                startFinal,
                endFinal,
            });
            void this._addTimeEntry(
                {
                    type,
                    employeeId: this.employee,
                    ...times,
                    position,
                    date,
                    breakPlanned,
                },
                this._activeEntry === data.entry.id || (type === TimeEntryType.Work && !times.startPlanned)
            );
        } else {
            const entry = this._timeEntries.find((e) => e.id === id);
            if (!entry) {
                return;
            }
            this._updateTimeEntry(entry, { date, startPlanned, endPlanned, startFinal, endFinal });
            if (this._activeEntry === entry.id) {
                this._setActive({
                    date: date,
                });
            }
        }

        (e.target as HTMLElement).classList.remove("dragover");

        setTimeout(() => this._dragend(), 50);
    }

    private _dragstart(e: DragEvent, data: DragData) {
        this._dragData = data;
        const el = e.target as HTMLElement;
        setVirtualizedDragImage(e);
        this._dropGuard.grab(e);
        const dt = e.dataTransfer!;
        dt.setData("text/plain", "42");
        dt.effectAllowed = "copyLink";
        this.classList.add("dragging");
        el.classList.add("dragging");
    }

    private _refresh() {
        if (!this._employee || !app.company || !this.dateRange) {
            return;
        }

        const entries = this._timeEntries.filter(
            (entry) => entry.date >= this.dateFrom && entry.date < this.dateTo && !app.isRemoved(entry)
        );

        // mark entries for result recalculation only if they are past the commit time
        const commitBefore = app.company.settings.commitTimeEntriesBefore;
        entries.forEach((entry) => {
            if (!commitBefore || entry.date >= commitBefore) {
                entry.result = null;
            }
        });

        // This mutates all time entries with result = null
        getDailyResults(
            app.company.settings,
            app.company,
            this._employee,
            this._timeEntries,
            this.dateRange,
            this._prevResults
        );

        const { from, to } = this.dateRange;
        const items: Item[] = [];

        let date = from;
        while (date < to) {
            const dayEntries = entries.filter((entry) => entry.date === date);
            const absence = this._absences.find(
                (a) =>
                    [AbsenceStatus.Approved, AbsenceStatus.Inferred].includes(a.status) &&
                    a.start <= date &&
                    a.end > date
            );
            if (dayEntries.length) {
                items.push(
                    ...dayEntries.map((timeEntry) => ({
                        date,
                        timeEntry,
                        absence,
                    }))
                );
            } else {
                items.push({ date, absence });
            }
            date = dateAdd(date, { days: 1 });
        }

        this._items = items;
        this._employeeDayForm && this._employeeDayForm.updateForms();
        this._loadStats();

        this._issues = getShiftIssues(
            this._employee,
            app.company.settings,
            app.company,
            this._timeEntries,
            this.dateRange
        );

        if (this._highlightedEntryId !== this._activeTimeEntryId) {
            queueMicrotask(() => {
                this._activeTimeEntryId && this._highlightTimeEntry(this._activeTimeEntryId);
            });
        }
    }

    private async _editHourAdjustment(entry: TimeEntry) {
        const edited = await this._hourAdjustmentDialog.show(entry);
        if (edited) {
            // synchronize in the background
            void this.synchronize(true);
        }
    }

    private _addHourAdjustment() {
        return this._editHourAdjustment(
            new TimeEntry({
                employeeId: this.employee!,
                date: defaultBookingDate(this._activeDate, this.dateRange),
                type: TimeEntryType.HourAdjustment,
                paid: false,
            })
        );
    }

    private async _editVacationAdjustment(entry: TimeEntry) {
        const edited = await this._vacationAdjustmentDialog.show(entry);
        if (edited) {
            // synchronize in the background
            void this.synchronize(true);
        }
    }

    private _addVacationAdjustment() {
        return this._editVacationAdjustment(
            new TimeEntry({
                employeeId: this.employee!,
                date: defaultBookingDate(this._activeDate, this.dateRange),
                type: TimeEntryType.VacationAdjustment,
                paid: false,
            })
        );
    }

    private async _editAbsence(absence: Absence) {
        const edited = await this._absenceDialog.show(absence);
        if (edited) {
            return this.synchronize(true);
        }
    }

    private async _addTimeEntry(vals: Partial<TimeEntry>, setActive = false) {
        if (![TimeEntryType.Work, TimeEntryType.CompDay, TimeEntryType.Free].includes(vals.type!)) {
            const absence = new Absence({
                type: vals.type,
                employeeId: vals.employeeId!,
                start: vals.date,
                end: dateAdd(vals.date!, { days: 1 }),
            });

            return this._editAbsence(absence);
        }

        const entry = new TimeEntry(vals);
        await app.createOrUpdateTimeEntries(entry, { applyAutoMeals: true });
        this._timeEntries.push(entry);
        this._refresh();

        if (setActive) {
            this._setActive({
                date: entry.date,
                entry: entry.id,
                field: "start",
            });
        }
        return;
    }

    private _updateTimeEntry(a: TimeEntry, data: Partial<TimeEntry> = {}) {
        if (app.isRemoved(a)) {
            return;
        }

        const { employeeId, ...rest } = data;
        Object.assign(a, rest);
        if (employeeId !== undefined) {
            assignEmployee(a, employeeId);
        }

        // push the changes to the server in the background
        void app.createOrUpdateTimeEntries(a);

        this._refresh();
    }

    private async _removeTimeEntry(entry: TimeEntry) {
        if (
            entry.isPast &&
            (entry.startFinal || entry.endFinal) &&
            !(await confirm("Sind Sie sicher dass Sie diesen Eintrag löschen möchten?", "Löschen", "Abbrechen", {
                title: "Eintrag Löschen",
                type: "destructive",
                icon: "icon.action.delete",
            }))
        ) {
            return;
        }

        if (entry.id === this._activeEntry) {
            this._setActive({ entry: null });
        }

        // push the changes to the server in the background
        void app.removeTimeEntries(entry);

        // filter out the removed entry in local state and refresh
        this._timeEntries = this._timeEntries.filter((e) => e.id !== entry.id);
        this._refresh();
    }

    // TODO: should just be number in browser, but our types are borked
    private _setActiveTimeout: number | NodeJS.Timeout;
    private _setActive(
        {
            date = this._activeDate,
            entry = this._activeEntry,
            field = this._activeField,
        }: {
            date?: DateString | null;
            entry?: string | null;
            field?: string | null;
        } = {},
        instant = false,
        e?: Event
    ) {
        e && e.stopPropagation();
        if (date && !entry) {
            const first = this._timeEntries.find((e) => e.date === date && !e.deleted);
            entry = (first && first.id) || "new";
        }

        const innerAsyncUpdate = async () => {
            this._activeDate = date;
            this._activeEntry = entry;
            this._activeField = field;
            await this.updateComplete;
            const row = this.renderRoot!.querySelector(".row.selected");
            if (row) {
                try {
                    // @ts-expect-error this is a non-standard method not yet supported by firefox
                    row.scrollIntoViewIfNeeded();
                } catch (e) {
                    row.scrollIntoView({ block: "center" });
                }
            }
        };

        clearTimeout(this._setActiveTimeout);

        if (entry || instant) {
            void innerAsyncUpdate();
        } else {
            this._setActiveTimeout = setTimeout(innerAsyncUpdate, 500);
        }
    }

    private _renderBonuses(bonuses: BonusResult[]) {
        const aggregated: {
            type: Pick<BonusType, "id" | "name">;
            taxed: Hours;
            taxFree: Hours;
        }[] = [];

        for (const bonus of bonuses) {
            if (!bonus.duration) {
                continue;
            }
            let existing = aggregated.find(({ type }) => type.id === bonus.type.id);
            if (!existing) {
                existing = {
                    type: bonus.type,
                    taxed: 0 as Hours,
                    taxFree: 0 as Hours,
                };

                aggregated.push(existing);
            }

            if (bonus.taxFree) {
                existing.taxFree = add(bonus.duration, existing.taxFree);
            } else {
                existing.taxed = add(bonus.duration, existing.taxed);
            }
        }

        return html`
            <table class="simple">
                <thead>
                    <tr>
                        <th></th>
                        <th>Beitragsfrei</th>
                        <th>Beitragspfl.</th>
                    </tr>
                </thead>
                <tbody>
                    ${aggregated.map(
                        ({ type, taxed, taxFree }) => html`
                            <tr>
                                <td><ptc-ui-icon name="icon.object.bonus"></ptc-ui-icon> ${type.name}</td>
                                <td>${taxFree ? toDurationString(taxFree) : "-"}</td>
                                <td>${taxed ? toDurationString(taxed) : "-"}</td>
                            </tr>
                        `
                    )}
                </tbody>
            </table>
        `;
    }

    private _renderCommittedLock(date: DateString) {
        const commitBefore = app.company?.settings.commitTimeEntriesBefore;
        if (!isCommitted(date, commitBefore)) {
            return "";
        }
        return html`
            <div
                ${popover(
                    html`
                        Dieser Tag liegt vor dem Festschreibungsdatum
                        <strong>${formatDate(commitBefore!)}</strong> und kann nicht mehr bearbeitet werden.
                    `,
                    { trigger: "hover", class: "tooltip non-interactive" }
                )}
            >
                <div class="duration">
                    <ptc-ui-icon name="icon.status.locked" label="Festgeschrieben"></ptc-ui-icon>
                </div>
            </div>
        `;
    }
    private _renderItem({ date, timeEntry: entry, absence }: Item) {
        const dep = entry?.position && app.getDepartment(entry.position.departmentId);
        const venue = (dep && dep.venue) || app.venues[0];
        const hol = getHolidayForDate(date, { holidays: venue?.enabledHolidays, country: app.company!.country });
        const employee = app.getEmployee(this.employee!)!;
        const contract = employee.getContractForDate(date);

        if (!entry) {
            if (!contract || contract.blocked) {
                return html`<div
                    class="center-aligning horizontal layout empty row border-bottom ${classMap({
                        sunday: new Date(date).getDay() === 0,
                        holiday: !!hol,
                    })}"
                    disabled
                >
                    <div class="pill status"><ptc-ui-icon name="icon.status.blocked"></ptc-ui-icon></div>

                    <div class="date horizontal layout">
                        ${hol
                            ? html` <div class="stretch ellipsis">${hol.name}</div> `
                            : html`
                                  <div class="weekday">
                                      ${["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][new Date(date).getDay()]}
                                  </div>
                                  <div class="stretch">${formatDate(date)}</div>
                              `}
                    </div>

                    <div class="stretch collapse">
                        <div class="comment ellipsis">${(contract && contract.comment) || "Inaktiv"}</div>
                    </div>
                </div>`;
            }

            return html`
                <div
                    class="center-aligning horizontal layout empty row border-bottom ${classMap({
                        sunday: new Date(date).getDay() === 0,
                        holiday: !!hol,
                        absence: !!absence,
                        selected: this._activeDate === date,
                        "absence-start": absence?.start === date,
                        "absence-end": absence?.end === dateAdd(date, { days: 1 }),
                    })}"
                    @click=${() => (absence ? this._editAbsence(absence) : this._setActive({ date, entry: "new" }))}
                    @dragenter=${(e: DragEvent) => this._dragenter(e, date)}
                    @dragleave=${this._dragleave}
                    @dragover=${(e: DragEvent) => this._dragoverDay(e, date)}
                    @drop=${(e: DragEvent) => this._dropIntoDay(e, date)}
                >
                    ${absence
                        ? html`
                              <div class="pill status" style="--color-highlight: ${timeEntryTypeColor(absence.type)}">
                                  <ptc-ui-icon name="icon.action.add" class="invisible"></ptc-ui-icon>
                              </div>
                          `
                        : html` <div class="pill status"><ptc-ui-icon name="icon.action.add"></ptc-ui-icon></div> `}

                    <div class="date horizontal layout">
                        ${hol
                            ? html` <div class="stretch ellipsis">${hol.name}</div> `
                            : html`
                                  <div class="weekday">
                                      ${["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][new Date(date).getDay()]}
                                  </div>
                                  <div class="stretch">${formatDate(date)}</div>
                              `}
                    </div>

                    <div class="stretch collapse"></div>

                    ${this._renderCommittedLock(date)}
                </div>
            `;
        }

        const start = entry.startFinal || entry.endFinal ? entry.startFinal : entry.startPlanned;
        const end = entry.endFinal || entry.endFinal ? entry.endFinal : entry.endPlanned;

        const salary = contract && getSalaryOrDefault(entry.positionId, contract.salaries);
        const commission = salary && salary.commission;
        const bonuses = entry.result?.bonuses?.filter((b) => !!b.duration) || [];
        const working = entry.type === TimeEntryType.Work;
        const finished = entry.startFinal && entry.endFinal;
        const currBreak =
            entry && entry.startBreak && Math.ceil((new Date().getTime() - entry.startBreak.getTime()) / 60000);
        const active = entry.startFinal && !entry.endFinal;
        const issues = this._issues.filter(
            (issue) => !issue.ignored && getShiftsForIssue(issue).some((shift) => shift.id === entry.id)
        );
        const noShow = issues.some((i) => i.type === IssueType.MissingShiftStart);
        const noLogout = issues.some((i) => i.type === IssueType.MissingShiftEnd);
        const shortBreak = issues.some((i) => i.type === IssueType.DeficientBreak);
        const tooLong = issues.some((i) => i.type === IssueType.ExcessiveWorkDay);
        const statutoryBreak = getStatutoryBreak(entry.durationFinal, app.company!.settings);
        const duration = entry.result?.base.duration || 0;
        const isHalfDay = isHalfDayEntry(entry);

        return html`
            <div
                class="center-aligning row border-bottom horizontal layout ${classMap({
                    sunday: new Date(date).getDay() === 0,
                    holiday: !!hol,
                    selected: this._activeDate === date && this._activeEntry === entry.id,
                    absence: !!absence,
                    "absence-start": absence?.start === date,
                    "absence-end": absence?.end === dateAdd(date, { days: 1 }),
                    [entry.status]: true,
                    "half-day": isHalfDay,
                })}"
                id="entry-${entry.id}"
                @click=${() =>
                    entry.type === TimeEntryType.Work
                        ? this._setActive({ date, entry: entry.id })
                        : entry.type === TimeEntryType.HourAdjustment
                          ? this._editHourAdjustment(entry)
                          : entry.type === TimeEntryType.VacationAdjustment
                            ? this._editVacationAdjustment(entry)
                            : absence
                              ? this._editAbsence(absence)
                              : this._setActive({ date, entry: entry.id })}
                @dragenter=${(e: DragEvent) => this._dragenter(e, date)}
                @dragleave=${this._dragleave}
                @dragover=${(e: DragEvent) => this._dragoverDay(e, date)}
                @drop=${(e: DragEvent) => this._dropIntoDay(e, date)}
                draggable="true"
                @dragstart=${(e: DragEvent) => this._dragstart(e, { entry })}
            >
                ${cache(
                    entry.type !== TimeEntryType.Work
                        ? html`<div class="pill status" style="--color-highlight: ${timeEntryTypeColor(entry.type)}">
                              <ptc-ui-icon name="${app.localized.timeEntryTypeIcon(entry.type)}"></ptc-ui-icon>
                          </div>`
                        : issues.length
                          ? html`
                                <div class="red pill status">
                                    <ptc-ui-icon name="icon.status.warning"></ptc-ui-icon>
                                </div>
                            `
                          : currBreak
                            ? html`
                                  <div class="orange pill status">
                                      <ptc-ui-icon name="icon.object.break"></ptc-ui-icon>
                                  </div>
                              `
                            : finished
                              ? html`
                                    <div class="green pill status">
                                        <ptc-ui-icon name="icon.status.success"></ptc-ui-icon>
                                    </div>
                                `
                              : active
                                ? html`
                                      <div class="blue pill status">
                                          <ptc-ui-icon name="icon.object.time"></ptc-ui-icon>
                                      </div>
                                  `
                                : html`
                                      <div class="pill status"><ptc-ui-icon name="icon.object.date"></ptc-ui-icon></div>
                                  `
                )}

                <div class="date horizontal layout">
                    ${hol
                        ? html` <div class="stretch ellipsis">${hol.name}</div> `
                        : html`
                              <div class="weekday">
                                  ${["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][new Date(date).getDay()]}
                              </div>
                              <div class="stretch">${formatDate(date)}</div>
                          `}
                </div>

                <div class="position horizontal center-aligning start-justifying layout" style="width: 20rem">
                    <div
                        class="pill ellipsis fit-horizontally"
                        style="--color-highlight: ${app.getTimeEntryColor(entry)}"
                    >
                        ${entry.position ? entry.position.name : app.localized.timeEntryTypeLabel(entry)}
                    </div>
                </div>

                <div class="start" ?hidden=${!working}>
                    <ptc-ui-time-input
                        compact
                        class="${noShow ? "warn" : ""} ${entry.isPast && !entry.startFinal ? "faded" : ""}"
                        icon="icon.action.start-shift"
                        name="startFinal"
                        .value=${live(toTimeString(start))}
                        readonly
                        @click=${(e: Event) => this._setActive({ date, entry: entry.id, field: "start" }, undefined, e)}
                    >
                    </ptc-ui-time-input>
                </div>

                <div class="end" ?hidden=${!working}>
                    <ptc-ui-time-input
                        compact
                        name="endFinal"
                        icon="icon.action.end-shift"
                        class="${noShow || noLogout ? "warn" : ""} ${entry.isPast && !entry.endFinal ? "faded" : ""}"
                        .value=${live(toTimeString(end))}
                        readonly
                        @click=${(e: Event) => this._setActive({ date, entry: entry.id, field: "end" }, undefined, e)}
                    >
                    </ptc-ui-time-input>
                </div>

                <div class="break" ?hidden=${!working}>
                    <ptc-ui-time-input
                        compact
                        name="break"
                        icon="icon.object.break"
                        class="${shortBreak ? "warn" : ""}"
                        .value=${live(
                            typeof entry.break === "number"
                                ? toDurationString(entry.break)
                                : typeof entry.breakPlanned === "number"
                                  ? toDurationString(entry.breakPlanned)
                                  : ""
                        )}
                        readonly
                        .hourDigits=${1}
                        @click=${(e: Event) => this._setActive({ date, entry: entry.id, field: "break" }, undefined, e)}
                        ?disabled=${!entry.final}
                        ${shortBreak
                            ? popover(
                                  html`
                                      Ges. Pause von
                                      <strong>${(statutoryBreak * 60).toFixed(0)} min</strong>
                                      unterschritten!
                                  `,
                                  { trigger: "hover", class: "red tooltip non-interactive" }
                              )
                            : nothing}
                    >
                    </ptc-ui-time-input>
                </div>

                <div class="meals" ?hidden=${!working}>
                    <div class="left icon input">
                        <ptc-ui-icon name="icon.object.meal"></ptc-ui-icon>
                        <input
                            type="number"
                            min="0"
                            max="3"
                            step="1"
                            name="meals"
                            .value=${(
                                (entry.mealsLunch || 0) +
                                (entry.mealsBreakfast || 0) +
                                (entry.mealsDinner || 0)
                            ).toString()}
                            readonly
                            @click=${(e: Event) =>
                                this._setActive({ date, entry: entry.id, field: "meals" }, undefined, e)}
                        />
                    </div>
                </div>

                <div class="revenue" ?hidden=${!working || !commission} title="Umsatz">
                    <div class="left icon input">
                        <ptc-ui-icon name="icon.object.currency"></ptc-ui-icon>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            name="revenue"
                            required
                            .value=${entry.revenue.toFixed(2)}
                            @click=${(e: Event) =>
                                this._setActive({ date, entry: entry.id, field: "revenue" }, undefined, e)}
                        />
                    </div>
                </div>

                <div class="stretch collapse"></div>

                ${this._renderCommittedLock(date)}
                ${entry.comment
                    ? html`
                          <div
                              ${popover(
                                  html` <ptc-ui-icon name="icon.object.comment"></ptc-ui-icon> ${entry.comment} `,
                                  {
                                      trigger: "hover",
                                      class: "tooltip non-interactive text-left-aligning",
                                      style: "min-width: 20em",
                                  }
                              )}
                          >
                              <div class="duration">
                                  <ptc-ui-icon name="icon.object.comment:active"></ptc-ui-icon>
                              </div>
                          </div>
                      `
                    : ""}
                ${bonuses.length
                    ? html`<div
                          ${popover(this._renderBonuses(bonuses), {
                              trigger: "hover",
                              class: "tooltip non-interactive text-left-aligning",
                              style: "min-width: 20em",
                          })}
                      >
                          <div class="duration">
                              <ptc-ui-icon name="icon.object.bonus:active"></ptc-ui-icon>
                          </div>
                      </div>`
                    : ""}

                <div
                    class="duration ${tooLong ? "warn" : ""}"
                    ?hidden=${entry.type === TimeEntryType.VacationAdjustment}
                >
                    <ptc-ui-icon name="icon.object.duration"></ptc-ui-icon>
                    ${toDurationString(duration, true)}
                </div>

                <div class="duration" ?hidden=${entry.type !== TimeEntryType.VacationAdjustment}>
                    <ptc-ui-icon name="${app.localized.timeEntryTypeIcon(TimeEntryType.Vacation)}"></ptc-ui-icon>
                    ${formatNumber(entry.days || 0, 2)}
                </div>
            </div>
        `;
    }

    private _renderSummary() {
        const displayPay = app.hasPermission("manage.employees.payroll");
        const timeEntries = this._timeEntries.filter(
            (entry) => entry.date >= this.dateFrom && entry.date < this.dateTo
        );

        const nominalHours = getNominalTime(
            app.company.settings,
            app.company,
            this._employee!,
            this.dateRange!,
            createAbsenceHoursGetter(this._prevResults)
        );

        const breakdown = getResultBreakDown(app.company!, timeEntries);
        const payrollReport = getPayrollReport(
            app.company!,
            this._employee!,
            timeEntries,
            this._advances,
            this.dateFrom,
            this.dateTo
        );

        return html`
            <div class="summary">
                <div>
                    <div class="tiny blue colored-text text-centering semibold">Soll</div>

                    <div>${toDurationString(nominalHours)}</div>
                </div>

                <div
                    ${popover(
                        html`
                            <table class="simple fill-horizontally">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th class="bold">Stunden</th>
                                        <th class="bold">Tage</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    ${breakdown.items.map((item) => {
                                        if (!item.result.base.duration && !item.result.days) {
                                            return;
                                        }
                                        return html`
                                            <tr
                                                style="--color-highlight: ${item.workArea?.position
                                                    ? app.getPositionColor(item.workArea?.position)
                                                    : timeEntryTypeColor(item.type)}"
                                            >
                                                <td class="stretch">
                                                    <div class="colored-text">
                                                        <ptc-ui-icon
                                                            name="${app.localized.timeEntryTypeIcon(item.type)}"
                                                        ></ptc-ui-icon>
                                                        ${item.comment ||
                                                        item.workArea?.position.name ||
                                                        app.localized.timeEntryTypeLabel({
                                                            type: item.type,
                                                        })}
                                                    </div>
                                                </td>
                                                <td>${toDurationString(item.result.base.duration)}</td>
                                                <td>${formatFraction(item.result.days)}</td>
                                            </tr>
                                        `;
                                    })}
                                </tbody>
                            </table>
                        `,
                        { trigger: "hover", style: "min-width: 18em;" }
                    )}
                >
                    <div class="tiny blue colored-text text-centering semibold">Ist</div>

                    <div>${toDurationString(breakdown.total.base.duration)}</div>
                </div>

                <div ${popover(this._renderBonuses(breakdown.total.bonuses), { trigger: "hover" })}>
                    <div class="tiny blue colored-text text-centering semibold">Zuschl.</div>

                    <div>
                        ${toDurationString(breakdown.total.bonuses.reduce((total, bonus) => total + bonus.duration, 0))}
                    </div>
                </div>

                ${displayPay && breakdown.total.commission
                    ? html`
                          <div>
                              <div class="tiny blue colored-text text-centering semibold">Umsatz</div>

                              <div>${formatNumber(breakdown.total.commission.revenue)} €</div>
                          </div>
                      `
                    : ""}
                ${displayPay
                    ? html`
                          <div
                              ${popover(
                                  html`
                                      <ptc-payroll-report
                                          .report=${payrollReport}
                                          style="min-width: 50em"
                                      ></ptc-payroll-report>
                                  `,
                                  { trigger: "hover" }
                              )}
                          >
                              <div class="tiny blue colored-text text-centering semibold">Lohn</div>

                              <div>${formatNumber(payrollReport.totalPay)} €</div>
                          </div>
                      `
                    : ""}
            </div>
        `;
    }

    private _renderDayMenu() {
        if (!this._employee || !this._activeDate) {
            return;
        }
        return html`
            <ptc-employee-day
                .entries=${this._timeEntries}
                .absences=${this._absences}
                .activeDate=${this._activeDate!}
                .activeEmployee=${this._employee!.id}
                .activeEntry=${this._activeEntry}
                .activeField=${this._activeField}
                .issues=${this._issues}
                .active=${!!this.active && !!this._activeDate}
                hideAvailabilities
                mode="time_sheet"
                @select=${({
                    detail: { date, entry, field, instant },
                }: CustomEvent<{
                    date?: DateString;
                    entry?: string | null;
                    field?: string | null;
                    instant?: boolean;
                }>) =>
                    this._setActive(
                        {
                            date,
                            entry,
                            field,
                        },
                        instant
                    )}
                @addentry=${(e: CustomEvent<Partial<TimeEntry>>) => this._addTimeEntry(e.detail, true)}
                @updateentry=${(e: CustomEvent<TimeEntry>) => this._updateTimeEntry(e.detail)}
                @close=${() => this._setActive({ date: null, entry: null, field: null }, true)}
                @remove=${(e: CustomEvent<{ entry: TimeEntry }>) => this._removeTimeEntry(e.detail.entry)}
                @begindrag=${(e: CustomEvent<{ data: DragData }>) => (this._dragData = e.detail.data)}
            ></ptc-employee-day>
        `;
    }

    render() {
        if (!this._employee) {
            return html`
                <div class="fullbleed center-aligning center-justifying vertical layout scrim">
                    <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
                </div>
            `;
        }

        return html`
            <div class="fullbleed vertical layout">
                <div class="padded center-aligning horizontal spacing layout">
                    <ptc-date-range-picker
                        @range-selected=${(e: CustomEvent<DateRange>) => this.go(null, e.detail)}
                        .range=${this.dateRange}
                        .maxDays=${70}
                    ></ptc-date-range-picker>

                    <div class="stretch"></div>

                    <button class="transparent slim">
                        <ptc-ui-icon name="icon.action.add"></ptc-ui-icon>
                    </button>

                    <ptc-popover class="popover-menu" hide-on-click>
                        <button @click=${() => this._addHourAdjustment()} class="small">
                            <ptc-ui-icon name="icon.object.cashbook"></ptc-ui-icon>
                            Zeitbuchung
                        </button>

                        <button @click=${() => this._addVacationAdjustment()} class="small">
                            <ptc-ui-icon name="icon.object.date"></ptc-ui-icon>
                            Urlaubsbuchung
                        </button>
                    </ptc-popover>

                    <button
                        class="transparent slim"
                        title="Arbeitszeiten Exportieren"
                        @click=${() =>
                            this.go("exports/time-sheet", {
                                "entity-filters": JSON.stringify([{ type: "employeeId", value: this._employee!.id }]),
                                ...this.dateRange,
                            })}
                    >
                        <ptc-ui-icon name="icon.action.download"></ptc-ui-icon>
                    </button>

                    ${this._renderSummary()}
                </div>

                <div class="horizontal layout stretch collapse">
                    <ptc-scroller class="stretch collapse">
                        <div class="padded center-aligning vertical layout">
                            <div data-virtualized>
                                ${virtualize({
                                    items: this._items,
                                    renderItem: (item: Item) => this._renderItem(item),
                                })}
                            </div>
                        </div>
                    </ptc-scroller>
                    ${this._renderDayMenu()}
                </div>
            </div>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>

            ${this._dropGuard.render()}
        `;
    }

    static styles = [
        shared,
        css`
            :host {
                display: flex;
                position: relative;
                flex-direction: column;
            }

            ptc-scroller:has(.highlight) {
                .row:not(.highlight) {
                    opacity: 0.2;
                }
            }

            ptc-employee-day {
                width: 250px;
                border-left: solid 1px var(--shade-1);
                border-right: solid 1px var(--shade-1);
            }

            .month-picker {
                max-width: 600px;
                padding-left: 0;
                padding-right: 0;
            }

            .summary {
                display: flex;
            }

            .summary > div {
                padding: 0.1em 0.5em;
            }

            .virtual-row:hover {
                z-index: 1;
            }

            .row {
                font-size: var(--font-size-small);
                padding: 0.5em 0.6em;
                cursor: pointer;
                height: 3em;
                box-sizing: border-box;
                width: 100%;
                min-width: 61em;
                box-sizing: border-box;
                transition: opacity 0.3s ease-in-out;
            }

            .row:hover,
            .row.selected,
            .row.dragover {
                background: var(--color-bg);
                border-radius: 0.5em;
                box-shadow: inset var(--shade-5) 0 0 0 0.1em;
            }

            .row.selected {
                box-shadow: inset var(--color-highlight, var(--color-primary)) 0 0 0 0.1em;
            }

            .row.dragover *,
            .row.drop-blocked * {
                pointer-events: none;
            }

            .row.drop-blocked {
                background: var(--color-negative-bg);
                border-radius: 0.5em;
                box-shadow: inset var(--color-negative) 0 0 0 0.1em;
            }

            .drop-blocked-tooltip {
                --tooltip-surface: var(--color-negative-bg);
                --tooltip-color: var(--color-negative);
                --tooltip-border-color: var(--color-negative);
                --tooltip-max-width: 30em;
            }

            .row.sunday .date {
                color: var(--orange);
            }

            .row.holiday .date {
                color: var(--violet);
            }

            .row.empty:not(:hover):not(.selected):not(.absence) > * {
                opacity: 0.5;
            }

            .row.half-day {
                .pill.status {
                    position: relative;

                    background: color-mix(in lab, var(--color-highlight), transparent);

                    &::before {
                        z-index: -1;
                        content: "";
                        display: block;
                        inset: -1px;
                        position: absolute;
                        background: var(--color-highlight);

                        clip-path: var(--path-diagonal-half);
                        border-radius: inherit;
                    }
                }
            }

            .row > :not(:first-child) {
                margin-left: 0.5em;
            }

            .row .date {
                font-weight: 600;
                width: 7.7em;
            }

            .row .date .weekday {
                width: 1.8em;
            }

            .position {
                width: 10em;
            }

            .position .pill.vacation {
                background: var(--blue-bg);
                color: var(--blue);
            }

            .position .pill.sick {
                background: var(--orange-bg);
                color: var(--orange);
            }

            .row .meals {
                width: 3.5em;
            }

            .row .revenue {
                width: 6.6em;
            }

            .row .duration {
                font-weight: 600;
                line-height: 2.5em;
            }

            .row .status {
                margin-right: 0.5em;
            }

            .row .status.pill ptc-ui-icon {
                display: inline-block;
            }

            .row.absence .status {
                height: 3em;
                border-radius: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                background: var(--color-highlight);
                color: var(--color-bg);
                border-color: transparent;
                box-sizing: border-box;
            }

            .row.absence.absence-start .status {
                border-top-left-radius: 0.5em;
                border-top-right-radius: 0.5em;
                height: 2.8em;
                position: relative;
                top: 0.2em;
                padding-top: 0;
            }

            .row.absence.absence-end .status {
                border-bottom-left-radius: 0.5em;
                border-bottom-right-radius: 0.5em;
                height: 2.8em;
                position: relative;
                top: -0.2em;
                padding-bottom: 0;
            }

            .row.absence.absence-start.absence-end .status {
                height: 2.3em;
                top: 0;
            }

            .warn {
                --time-input-color: var(--color-negative);
                --time-input-border-color: var(--color-negative);
            }

            .row .duration ptc-ui-icon {
                margin-right: -0.2em;
            }

            input {
                padding-top: 0.3em;
                padding-bottom: 0.3em;
                padding-right: 0.3em;
                cursor: pointer !important;
            }
        `,
    ];
}
